import { memo, useState } from 'react';

import { UserPopover } from '@bonusly/common-user-popover';
import { Box, Link, useBreakpointValue, useDisclosure } from '@bonusly/design-system';
import { useBuildRecognizeURL } from '@bonusly/url';

import { Bonus } from '../../../../../types/bonus';
import { UserAvatar } from '../../components/user-avatar';
import { Route } from '../../config/router';
import { BonusFeedTrackedAction, useTrackBonusFeedItemEvent } from '../../hooks/use-bonus-feed-item-analytics';
import { pathWithId } from '../../utils/api';
import { CommentDisplay } from './comment-display';
import { CommentEditor } from './comment-editor';
import { ScrollToCommentData } from './hooks/use-scroll-to-comment';

interface CommentProps {
	bonus: Bonus;
	parentBonus: Bonus;
	scrollTo?: ScrollToCommentData;
}

export const Comment = memo(({ bonus, parentBonus, scrollTo }: CommentProps) => {
	const recognizeURL = useBuildRecognizeURL();
	const { isOpen: isEditing, onClose, onOpen } = useDisclosure();
	const { giver } = bonus;
	const trackAvatarClick = useTrackBonusFeedItemEvent(bonus, BonusFeedTrackedAction.AvatarClick);

	const [hasScrolledTo, setHasScrolledTo] = useState(false);
	if (scrollTo?.scrollToAction && !hasScrolledTo) {
		setHasScrolledTo(true);
		setTimeout(scrollTo.scrollToAction, 500);
		setTimeout(scrollTo.scrollToAction, 1500);
		setTimeout(scrollTo.scrollToAction, 2500);
	}

	const sizes = useBreakpointValue({ base: 'sm' as any, xs: 'md' as any });

	return (
		<Box display="flex" data-testid="comment" ref={scrollTo?.commentRef}>
			<UserPopover userId={giver.id}>
				<Link href={recognizeURL(pathWithId(Route.UserProfile, giver.id))} _hover={{ textDecoration: 'none' }}>
					<UserAvatar user={giver} onClick={trackAvatarClick} size={sizes} />
				</Link>
			</UserPopover>

			{isEditing ? (
				<CommentEditor bonus={bonus} onDone={onClose} parentBonus={parentBonus} />
			) : (
				<CommentDisplay bonus={bonus} parentBonus={parentBonus} onEdit={onOpen} highlightColor={scrollTo?.highlightColor} />
			)}
		</Box>
	);
});

Comment.displayName = 'Comment';
